import React, { useState } from 'react';
import Icon from '../Components/Icon';
import styled, { css, keyframes } from 'styled-components';
import { getColor } from '../styling/mixins/colors';
import {
  fontFamily,
  fontFamilyBlack,
  fontFamilyBold,
  fontFamilyMedium,
} from '../styling/mixins/typography';
import { breakpoint } from '../styling/mixins/breakpoints';

const StyledAccordionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  padding-top: 2.4rem;
  padding-bottom: 3.2rem;

  ${breakpoint('medium')} {
    padding-top: 3.2rem;
    padding-bottom: 4rem;
  }

  ${breakpoint('large')} {
    padding-top: 4rem;
    padding-bottom: 4.8rem;
  }
`;

const StyledTitleContainer = styled.div`
  background-color: ${getColor('gray', 900)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.6rem;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const StyledTitle = styled.h6`
  color: ${getColor('white')};
  text-transform: uppercase;
`;

const StyledIcon = styled(Icon)`
  width: 2.4rem;
  height: 2.4rem;
`;

const AnimationRotateUp = keyframes`
  from { transform: rotate(-180deg) }
  to { transform: rotate(0) }
`;

const StyledIconUp = styled(StyledIcon)`
  animation: ${AnimationRotateUp} 0.3s ease-in;
`;

const AnimationRotateDown = keyframes`
  from { transform: rotate(0) }
  to { transform: rotate(-180deg) }
`;

const StyledIconDown = styled(StyledIcon)`
  animation: ${AnimationRotateDown} 0.3s ease-in;
  transform: rotate(180deg);
`;

const StyledBody = styled.div`
  padding: 1.6rem 1.6rem 0.8rem 1.6rem;
  ${fontFamily};
  font-size: 1.4rem;
  line-height: 2.1rem;
  & p {
    padding-bottom: 1.2rem;
  }
`;

const BodyContainerStyle = css`
  color: ${getColor('gray', 900)};
  ${fontFamilyBold};
  font-size: 1.6rem;
  line-height: 1.9rem;
  overflow: hidden;
  border: 1px solid ${getColor('grey', 900)};
`;

const AnimationFadeIn = keyframes`
  from { max-height: 0 }
  to { max-height: 1500px }
`;

const StyledAccordionOpen = styled.div`
  ${BodyContainerStyle};
  animation: ${AnimationFadeIn} 0.3s ease-in;
`;

const AnimationFadeOut = keyframes`
  from { max-height: 1500px }
  to { max-height: 0 }
`;

const StyledAccordionClosed = styled.div`
  ${BodyContainerStyle};
  animation: ${AnimationFadeOut} 0.3s ease-out;
`;

interface AccordionProps {
  title: string;
  content: string;
  className?: string;
}

const Accordion = (props: AccordionProps) => {
  const className = props.className;

  const [isAccordionOpen, setAccordionOpen] = useState(false);
  const [isAccordionHidden, setAccordionHidden] = useState(true);

  const toggleVisibility = async () => {
    setAccordionOpen(!isAccordionOpen);

    if (isAccordionOpen) {
      await new Promise((r) => setTimeout(r, 300));
      setAccordionHidden(true);
    } else {
      setAccordionHidden(false);
    }
  };

  return (
    <div className={`${className ?? ''}`}>
      <StyledTitleContainer onClick={toggleVisibility}>
        <StyledTitle>{props.title}</StyledTitle>

        {isAccordionOpen && <StyledIconUp name="arrow-up" />}

        {!isAccordionOpen && <StyledIconDown name="arrow-up" />}
      </StyledTitleContainer>

      {isAccordionOpen && !isAccordionHidden && (
        <StyledAccordionOpen>
          <StyledBody
            dangerouslySetInnerHTML={{ __html: props.content }}
          />
        </StyledAccordionOpen>
      )}

      {!isAccordionOpen && !isAccordionHidden && (
        <StyledAccordionClosed>
          <StyledBody
            dangerouslySetInnerHTML={{ __html: props.content }}
          />
        </StyledAccordionClosed>
      )}
    </div>
  );
};

const StyledAccordion = styled(Accordion)`
  width: 100%;
  max-width: 66rem;
`;

interface AccordionBlockProps {
  accordions: AccordionProps[];
}

const AccordionBlock = (props: AccordionBlockProps) => {
  const elements = props.accordions.map((item, idx) => (
    <StyledAccordion
      title={item.title}
      content={item.content}
      key={item.title + idx}
    />
  ));

  return <StyledAccordionContainer>{elements}</StyledAccordionContainer>;
};

export default AccordionBlock;
